import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormField } from "../../../form/component/molecules/FormField";
import { API_BASE_URL } from "../../../config/api";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";

// strict list of statuses that still require approval
const PENDING_APPROVAL_SET = new Set([
  "awaiting hod approval",
  "awaiting hr operations senior manager approval",
  "awaiting cfo approval",
  "awaiting general manager human resources approval",
  "awaiting ceo approval",
]);

export const RecruitmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const stripHtml = (html = "") =>
    String(html)
      .replace(/<\/(p|div|br|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const [formData, setFormData] = useState({
    department: "",
    positionType: "",
    title: "",
    jobLevel: "",
    manager: "",
    positionDuration: "",
    motivationNote: "",
    rawAttachmentPath: "",
    storedName: "",
  });

  const [currentStatus, setCurrentStatus] = useState("");

  const toast = useCallback((title, message) => {
    notifications.show({
      title,
      message,
      autoClose: 1500,
      withCloseButton: false,
      color: "teal",
      icon: <IconCheck size={16} />,
      radius: "md",
      styles: { root: { maxWidth: 260, padding: "8px 10px" } },
    });
  }, []);

  // fetch request
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !id) return;

    fetch(`${API_BASE_URL}/api/requests/${id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Error: ${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((payload) => {
        const r = payload?.data ?? {};

        setCurrentStatus(String(r.status || "").trim());

        // if this request is already past the approval phase, go back to the table
        const s = String(r.status || "").trim().toLowerCase();
        if (s && !PENDING_APPROVAL_SET.has(s)) {
          toast("Done", "Request moved out of approval.");
          setTimeout(() => navigate("/approval", { replace: true }), 700);
          return;
        }

        let note =
          r.motivationNotes ??
          r.motivationNote ??
          r.motivation_description ??
          r.motivation ??
          "";

        if (note && typeof note === "object") note = note.text ?? note.value ?? JSON.stringify(note);
        if (Array.isArray(note)) note = note.filter(Boolean).join("\n");
        note = typeof note === "string" ? stripHtml(note.trim()) : "";

        const rawPath = r.motivationAttachment || "";
        const storedName = rawPath ? rawPath.split(/[\\/]/).pop() : "";

        setFormData({
          department: r.department || "",
          positionType: r.positionType || "",
          title: r.positionTitle || "",
          jobLevel: r.jobLevel || "",
          manager: r.hiringmanagersname || r.hiringManager || "",
          positionDuration: r.positionDuration || "",
          motivationNote: note,
          rawAttachmentPath: rawPath,
          storedName,
        });
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [id, navigate, toast]);

  // listen for global approval completion (emitted by the Approve action elsewhere)
  useEffect(() => {
    const onApproved = () => {
      toast("Approved", "Request approved successfully.");
      const t = setTimeout(() => navigate("/approval", { replace: true }), 700);
      return () => clearTimeout(t);
    };
    window.addEventListener("approval:done", onApproved);
    return () => window.removeEventListener("approval:done", onApproved);
  }, [navigate, toast]);

  const ringYellow =
    "focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const readOnlyBase =
    // force pure white for disabled inputs too
    `w-full border border-gray-300 rounded px-3 py-2 text-black bg-white disabled:bg-white ${ringYellow}`;
  const mtnFont =
    '"MTN Brighter Sans","MTN Bright Sans","Inter","Segoe UI",system-ui,-apple-system,sans-serif';

  const downloadUrl = useMemo(() => {
    if (!formData.storedName) return "";
    const token = localStorage.getItem("token") || "";
    const base = `${API_BASE_URL}/api/requests/files/${encodeURIComponent(formData.storedName)}`;
    return token ? `${base}?auth=${encodeURIComponent(token)}` : base;
  }, [formData.storedName]);

  const hasAttachment = Boolean(formData.storedName);

  const openAttachment = () => {
    if (!hasAttachment) {
      toast("No Attachment", "There is no file for this request.");
      return;
    }
    toast("Opening", "Attachment is opening...");
    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  return (
    // full-width white form; no top gap, no gray background
    <div
      className="w-full bg-white"
      style={{ fontFamily: mtnFont }}
    >
      <form className="space-y-4">
        <FormField label="Requesting Department">
          <input type="text" value={formData.department} disabled className={readOnlyBase} />
        </FormField>

        <FormField label="Position Type">
          <input type="text" value={formData.positionType} disabled className={readOnlyBase} />
        </FormField>

        <FormField label="Position Title">
          <input type="text" value={formData.title} disabled className={readOnlyBase} />
        </FormField>

        <FormField label="Job Level">
          <input type="text" value={formData.jobLevel} disabled className={readOnlyBase} />
        </FormField>

        <FormField label="Hiring Manager’s Name">
          <input type="text" value={formData.manager} disabled className={readOnlyBase} />
        </FormField>

        {formData.positionDuration && (
          <FormField label="Position Duration">
            <input type="text" value={formData.positionDuration} disabled className={readOnlyBase} />
          </FormField>
        )}

        <FormField label="Motivation Description (Optional)">
          <textarea
            rows={4}
            value={formData.motivationNote}
            disabled
            className={`w-full p-3 border border-gray-300 rounded text-black placeholder-gray-400 bg-white disabled:bg-white ${ringYellow}`}
            placeholder={formData.motivationNote ? "" : "No motivation provided"}
            style={{ whiteSpace: "pre-wrap" }}
          />
        </FormField>

        <FormField label="Motivation Attachment (Optional)">
          <div
            role="button"
            tabIndex={0}
            onClick={openAttachment}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openAttachment()}
            className={`w-full border border-gray-300 rounded bg-white ${ringYellow} ${
              hasAttachment ? "cursor-pointer" : "cursor-not-allowed opacity-80"
            }`}
            style={{ padding: 0 }}
            title={hasAttachment ? "Open attachment" : "No attachment"}
          >
            <div className="flex items-center gap-3 px-2 py-1">
              <div className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded bg-white">
                Choose file
              </div>
              <div className={`flex-1 truncate ${hasAttachment ? "text-black" : "text-gray-400"}`}>
                {hasAttachment ? formData.storedName : "No file chosen"}
              </div>
            </div>
          </div>

          {!hasAttachment && (
            <p className="mt-1 text-sm text-gray-500">No attachment available for this request.</p>
          )}
        </FormField>
      </form>
    </div>
  );
};
